import { useEffect, useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Select } from '../ui/Select';
import { tenantUsersAPI } from '../../api';
import { useAuthStore } from '../../store/authStore';

const COLOR_OPTIONS = [
  { value: 'blue', label: 'Blue', swatch: 'bg-blue-500' },
  { value: 'red', label: 'Red', swatch: 'bg-red-500' },
  { value: 'green', label: 'Green', swatch: 'bg-emerald-500' },
  { value: 'yellow', label: 'Yellow', swatch: 'bg-amber-500' },
  { value: 'purple', label: 'Purple', swatch: 'bg-violet-500' },
  { value: 'orange', label: 'Orange', swatch: 'bg-orange-500' },
  { value: 'gray', label: 'Gray', swatch: 'bg-gray-500' },
];

const REMINDER_PRESETS = [
  { label: 'No reminder', value: 'none' },
  { label: '5 minutes before', value: '5' },
  { label: '30 minutes before', value: '30' },
  { label: '1 hour before', value: '60' },
  { label: '1 day before', value: '1440' },
  { label: 'Custom datetime', value: 'custom' },
];

const toIsoFromDate = (value) => {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
};

const toIsoFromDateTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
};

const formatForDateInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const formatForDateTimeInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
};

export default function PlannerItemModal({
  open,
  onOpenChange,
  item,
  draftSelection,
  onSave,
  onDelete,
  onToggleDone,
  onAcceptAssignment,
  onRejectAssignment,
}) {
  const { user } = useAuthStore();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [itemType, setItemType] = useState('task');
  const [allDay, setAllDay] = useState(false);
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [color, setColor] = useState('blue');
  const [reminderPreset, setReminderPreset] = useState('none');
  const [customReminder, setCustomReminder] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [tenantUsers, setTenantUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isEditing = Boolean(item?.id);
  const isPendingAssignment =
    item?.assignment_status === 'pending' && item?.assigned_to === user?.id;

  const canAssign = itemType === 'task';

  const assignmentLabel = useMemo(() => {
    if (!item?.assigned_to) return 'Unassigned';
    const assigned = tenantUsers.find((u) => u.id === item.assigned_to);
    if (!assigned) return 'Assigned user';
    const name = [assigned.firstName, assigned.lastName].filter(Boolean).join(' ');
    return name || assigned.email || 'Assigned user';
  }, [item, tenantUsers]);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const response = await tenantUsersAPI.list();
        setTenantUsers(response.users || []);
      } catch (err) {
        console.error('Failed to load tenant users', err);
      }
    };
    if (open) {
      loadUsers();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setError('');
    if (item) {
      setTitle(item.title || '');
      setDescription(item.description || '');
      setItemType(item.item_type || 'task');
      setAllDay(Boolean(item.all_day));
      setStartAt(item.all_day ? formatForDateInput(item.start_at) : formatForDateTimeInput(item.start_at));
      setEndAt(item.all_day ? formatForDateInput(item.end_at) : formatForDateTimeInput(item.end_at));
      setColor(item.color || 'blue');
      setAssignedTo(item.assigned_to || '');
      setReminderPreset('none');
      setCustomReminder('');
      return;
    }

    setTitle('');
    setDescription('');
    setItemType('task');
    setAllDay(Boolean(draftSelection?.allDay));
    if (draftSelection?.allDay) {
      setStartAt(draftSelection?.startAt ? formatForDateInput(draftSelection.startAt) : '');
      setEndAt(draftSelection?.endAt ? formatForDateInput(draftSelection.endAt) : '');
    } else {
      setStartAt(draftSelection?.startAt ? formatForDateTimeInput(draftSelection.startAt) : '');
      setEndAt(draftSelection?.endAt ? formatForDateTimeInput(draftSelection.endAt) : '');
    }
    setColor('blue');
    setAssignedTo('');
    setReminderPreset('none');
    setCustomReminder('');
  }, [item, open, draftSelection]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      if (!title.trim()) {
        setError('Title is required.');
        return;
      }

      const startIso = allDay ? toIsoFromDate(startAt) : toIsoFromDateTime(startAt);
      const endIso = allDay ? toIsoFromDate(endAt) : toIsoFromDateTime(endAt);

      if (!startIso) {
        setError('Start date/time is required.');
        return;
      }

      let reminderOffsetMinutes;
      let reminderAt;
      if (reminderPreset === 'custom') {
        reminderAt = toIsoFromDateTime(customReminder);
      } else if (reminderPreset !== 'none') {
        reminderOffsetMinutes = Number(reminderPreset);
      }

      await onSave({
        title: title.trim(),
        description: description.trim() || null,
        startAt: startIso,
        endAt: endIso,
        allDay,
        itemType,
        color,
        reminderOffsetMinutes,
        reminderAt,
        assignedTo: canAssign && assignedTo ? assignedTo : undefined,
      });
    } catch (err) {
      console.error('Failed to save planner item', err);
      setError(err.message || 'Failed to save planner item.');
    } finally {
      setSaving(false);
    }
  };

  const handleAccept = async () => {
    if (!item?.id) return;
    try {
      setSaving(true);
      setError('');
      await onAcceptAssignment(item.id);
    } catch (err) {
      console.error('Failed to accept assignment', err);
      setError(err.message || 'Failed to accept assignment.');
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async () => {
    if (!item?.id) return;
    try {
      setSaving(true);
      setError('');
      await onRejectAssignment(item.id);
    } catch (err) {
      console.error('Failed to reject assignment', err);
      setError(err.message || 'Failed to reject assignment.');
    } finally {
      setSaving(false);
    }
  };

  const renderAssignmentSelect = () => {
    if (!canAssign) return null;

    return (
      <div className="space-y-2">
        <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">Assign to</label>
        <Select
          value={assignedTo || ''}
          onChange={(event) => setAssignedTo(event.target.value)}
          className="h-8"
        >
          <option value="">Unassigned</option>
          {tenantUsers.map((userOption) => {
            const label = [userOption.firstName, userOption.lastName].filter(Boolean).join(' ') || userOption.email;
            return (
              <option key={userOption.id} value={userOption.id}>
                {label}
              </option>
            );
          })}
        </Select>
      </div>
    );
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-[var(--ds-radius-lg)] bg-[var(--ds-surface-panel)] border border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-popover)]">
          <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--ds-border-divider)]">
            <Dialog.Title className="text-h2 text-[var(--ds-text-primary)] font-semibold">
              {isEditing ? 'Edit Planner Item' : 'Create Planner Item'}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                className="p-1.5 text-[var(--ds-text-muted)] hover:text-[var(--ds-text-primary)] hover:bg-[var(--ds-control-field-hover-bg)] rounded-md transition-colors"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>

          <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
            {error && (
              <div className="text-xs text-[var(--ds-status-danger)]">{error}</div>
            )}

            <div className="space-y-2">
              <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
              <Input value={title} onChange={(event) => setTitle(event.target.value)} className="h-8" />
            </div>

            <div className="space-y-2">
              <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
              <Textarea value={description} onChange={(event) => setDescription(event.target.value)} className="text-xs" />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">Item type</label>
                <Select value={itemType} onChange={(event) => setItemType(event.target.value)} className="h-8">
                  <option value="task">Task</option>
                  <option value="event">Event</option>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium text-[var(--ds-text-primary)]">All day</label>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={allDay}
                    onChange={(event) => setAllDay(event.target.checked)}
                    className="h-4 w-4"
                  />
                  <span className="text-xs text-[var(--ds-text-secondary)]">Treat as all-day item</span>
                </div>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">Start</label>
                <Input
                  type={allDay ? 'date' : 'datetime-local'}
                  value={startAt}
                  onChange={(event) => setStartAt(event.target.value)}
                  className="h-8"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">End</label>
                <Input
                  type={allDay ? 'date' : 'datetime-local'}
                  value={endAt}
                  onChange={(event) => setEndAt(event.target.value)}
                  className="h-8"
                />
              </div>
            </div>

            {renderAssignmentSelect()}

            {item?.assigned_to && (
              <div className="text-xs text-[var(--ds-text-secondary)]">
                Assigned to: <span className="text-[var(--ds-text-primary)]">{assignmentLabel}</span>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-xs font-medium text-[var(--ds-text-primary)]">Color</label>
              <div className="flex flex-wrap gap-2">
                {COLOR_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setColor(option.value)}
                    className={`h-8 w-8 rounded-full border ${
                      color === option.value
                        ? 'border-[var(--ds-accent-primary)] ring-2 ring-[var(--ds-accent-primary)]/40'
                        : 'border-[var(--ds-border-subtle)]'
                    } ${option.swatch}`}
                    aria-label={option.label}
                  />
                ))}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">Reminder</label>
                <Select value={reminderPreset} onChange={(event) => setReminderPreset(event.target.value)} className="h-8">
                  {REMINDER_PRESETS.map((preset) => (
                    <option key={preset.value} value={preset.value}>
                      {preset.label}
                    </option>
                  ))}
                </Select>
              </div>
              {reminderPreset === 'custom' && (
                <div className="space-y-2">
                  <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">Custom reminder</label>
                  <Input
                    type="datetime-local"
                    value={customReminder}
                    onChange={(event) => setCustomReminder(event.target.value)}
                    className="h-8"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="px-6 py-4 border-t border-[var(--ds-border-divider)] flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-2">
              {isEditing && itemType === 'task' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onToggleDone(item.id)}
                  disabled={saving || isPendingAssignment}
                >
                  {item?.is_done ? 'Mark as not done' : 'Mark as done'}
                </Button>
              )}
              {isEditing && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => onDelete(item.id)}
                  disabled={saving}
                >
                  Delete
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2 justify-end">
              {isPendingAssignment && (
                <>
                  <Button variant="outline" size="sm" onClick={handleReject} disabled={saving}>
                    Reject
                  </Button>
                  <Button variant="primary" size="sm" onClick={handleAccept} disabled={saving}>
                    Accept
                  </Button>
                </>
              )}
              {!isPendingAssignment && (
                <>
                  <Dialog.Close asChild>
                    <Button variant="outline" size="sm" disabled={saving}>
                      Cancel
                    </Button>
                  </Dialog.Close>
                  <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </>
              )}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
